import { Theme } from "@mui/material";
import Icon from "@mui/material/Icon";
import Guid from "_shared/guids/guid";
import Individual from "marketplace/individuals/individual";
import { isTemplateRow, isPermissionsRow } from "permissions/_views/tables/_utilities/utils";
import GroupedPermissionCard from "permissions/_views/tables/cards/grouped-permission-card";
import GroupedTemplatePermissionCard from "permissions/_views/tables/cards/grouped-template-permission-card";
import SinglePermissionCard from "permissions/_views/tables/cards/single-permission-card";
import SingleTemplatePermissionCard from "permissions/_views/tables/cards/single-template-permission-card";
import Permission from "permissions/permission";
import type { Resource } from "permissions/resources/permission-resource";
import {
  TableRow,
  UniversalCellConfig,
  CategoryLabel,
  ControlMeta,
  CardType,
} from "permissions/_views/types/permission-types";
import { JSX } from "react";

type PermissionCardProps = {
  row: TableRow | null;
  config: UniversalCellConfig;
  isSingleGranteeView: boolean;
  isCreateTemplateView: boolean;
  isManageTemplatesView: boolean;
  theme: Theme;
  activeCategory: CategoryLabel | "";
  onTogglePermission(permission: Permission | undefined): void;
  onToggleTemplate(resource: Resource | undefined): void;
  onApplyPermissionMeta(effective: Permission, resource: Resource, meta: ControlMeta, newValue: string): void;
  onApplyTemplateMeta(resource: Resource, meta: ControlMeta, newValue: string): void;
  individuals: Individual[];
  loadingIndividualUserIds: Guid[];
  getCardSubheader(resource: Resource): string | undefined;
};

export default function PermissionCard(props: Readonly<PermissionCardProps>): JSX.Element {
  const {
    row,
    config,
    isSingleGranteeView,
    isCreateTemplateView,
    isManageTemplatesView,
    theme,
    activeCategory,
    onTogglePermission,
    onToggleTemplate,
    onApplyPermissionMeta,
    onApplyTemplateMeta,
    individuals,
    loadingIndividualUserIds,
    getCardSubheader,
  } = props;

  if (config.cardType === CardType.Single) {
    if (isManageTemplatesView && isTemplateRow(row)) {
      const resource = config.candidates(row, config.resource)[0] as Resource | undefined;
      return (
        <SingleTemplatePermissionCard
          canEdit={config.canEdit || !row?.id}
          resource={resource ?? config.template?.(config.resource)}
          config={config}
          theme={theme}
          activeCategory={activeCategory}
          getCardSubheader={getCardSubheader}
          onToggle={onToggleTemplate}
          onApplyMeta={onApplyTemplateMeta}
        />
      );
    }

    if (isPermissionsRow(row)) {
      return (
        <SinglePermissionCard
          row={row}
          config={config}
          isSingleGranteeView={isSingleGranteeView}
          isCreateTemplateView={isCreateTemplateView}
          isManageTemplatesView={isManageTemplatesView}
          theme={theme}
          activeCategory={activeCategory}
          individuals={individuals}
          loadingIndividualUserIds={loadingIndividualUserIds}
          getCardSubheader={getCardSubheader}
          onToggle={onTogglePermission}
          onApplyMeta={onApplyPermissionMeta}
        />
      );
    }
  }
  if (config.cardType === CardType.Grouped) {
    if (isManageTemplatesView && isTemplateRow(row)) {
      return (
        <GroupedTemplatePermissionCard
          row={row}
          canEdit={config.canEdit || !row?.id}
          config={config}
          theme={theme}
          activeCategory={activeCategory}
          getCardSubheader={getCardSubheader}
          onToggle={onToggleTemplate}
          onApplyMeta={onApplyTemplateMeta}
        />
      );
    }

    if (isPermissionsRow(row)) {
      return (
        <GroupedPermissionCard
          row={row}
          config={config}
          isSingleGranteeView={isSingleGranteeView}
          isCreateTemplateView={isCreateTemplateView}
          isManageTemplatesView={isManageTemplatesView}
          theme={theme}
          activeCategory={activeCategory}
          getCardSubheader={getCardSubheader}
          onToggle={onTogglePermission}
          onApplyMeta={onApplyPermissionMeta}
        />
      );
    }
  }

  return <Icon>remove</Icon>;
}
